import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  TextField,
  Alert,
  Divider,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import CrewMemberList from "./CrewMemberList";
import type {
  SiteVisitData,
  SiteVisitPayload,
  SiteVisitVenue,
  CrewMember,
} from "./site-visit.types";
import SelectedCrewChips from "./SelectedCrewMemberChip";
import VenueDetails from "./VenueDetails";
import apiService from "../../../../service/Api";

interface ScheduleSiteVisitModalProps {
  open: boolean;
  onClose: () => void;
  venueId: number;
  venue: SiteVisitVenue;

  onSuccess?: () => void;
}

export type PaginatedResponse<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

export const calculateNextSiteVisitDate = (assignedDate: string): Date => {
  const date = new Date(assignedDate);
  date.setMonth(date.getMonth() + 3);
  return date;
};

export const formatDisplayDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const ScheduleSiteVisitModal: React.FC<ScheduleSiteVisitModalProps> = ({
  open,
  onClose,
  venueId,
  venue,
  onSuccess,
}) => {
  const [siteVisitData, setSiteVisitData] = useState<SiteVisitData | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [scheduledDate, setScheduledDate] = useState<string>("");
  const [selectedCrewMembers, setSelectedCrewMembers] = useState<CrewMember[]>(
    [],
  );
  const [triggerAiAgent, setTriggerAiAgent] = useState<boolean>(false);

  // Calculate next site visit date (3 months from assigned date)

  // Format date for display

  const fetchSiteVisitStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get<PaginatedResponse<SiteVisitData>>(
        `/api/venues/site-visit/?venue=${venueId}`,
      );

      const firstResult = response.data?.results?.[0] ?? null;

      setSiteVisitData(firstResult);
    } catch (err) {
      console.error("Failed to fetch site visit status:", err);
      setError("Failed to load site visit information");
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => {
    if (open) {
      fetchSiteVisitStatus();
      setScheduledDate("");
      setSelectedCrewMembers([]);
      setTriggerAiAgent(false);
    }
  }, [open, fetchSiteVisitStatus]);

  const handleSelectMember = (member: CrewMember) => {
    setSelectedCrewMembers((prev) => {
      if (prev.some((m) => m.id === member.id)) {
        return prev;
      }
      return [...prev, member];
    });
  };

  const handleDeselectMember = (member: CrewMember) => {
    setSelectedCrewMembers((prev) => prev.filter((m) => m.id !== member.id));
  };

  const handleScheduleSiteVisit = async () => {
    if (!scheduledDate || selectedCrewMembers.length === 0) {
      setError("Please select a date and at least one crew member");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const assigneeIds = selectedCrewMembers.map((m) => m.user?.id);
      const scheduledDateTimeISO = new Date(scheduledDate).toISOString();

      const payload: SiteVisitPayload = {
        scheduled_date_time: scheduledDateTimeISO,
        assignee_ids: assigneeIds,
        notes: null,
        status: "assigned",
        trigger_ai_agent: triggerAiAgent,
      };

      if (siteVisitData?.id) {
        await apiService.patch(
          `/api/venues/site-visit/${siteVisitData.id}/`,
          payload,
        );
      } else {
        await apiService.post("/api/venues/site-visit/", {
          venue: venueId,
          ...payload,
        });
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error("Failed to schedule site visit:", err);
      setError("Failed to schedule site visit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setScheduledDate("");
    setSelectedCrewMembers([]);
    setTriggerAiAgent(false);
    setError(null);
    onClose();
  };

  const isPending = siteVisitData?.status === "pending";
  const isAssigned = siteVisitData?.status === "assigned";
  const noRecord = !loading && siteVisitData === null;

  const nextSiteVisitDate =
    isAssigned && siteVisitData?.scheduled_date_time
      ? calculateNextSiteVisitDate(siteVisitData.scheduled_date_time)
      : null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: "90vh" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography fontWeight={600}>Schedule Site Visit</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* Loading State */}
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 8,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Content */}
        {!loading && (noRecord || siteVisitData) && (
          <>
            {/* Venue Details */}
            <VenueDetails venue={venue} />

            {/* Already Assigned - Show Next Visit Info */}
            {isAssigned && nextSiteVisitDate && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Site visit already scheduled</strong>
                </Typography>
                <Typography variant="body2">
                  Last scheduled:{" "}
                  {formatDisplayDate(siteVisitData!.scheduled_date_time!)}
                </Typography>
                <Typography variant="body2">
                  Next site visit due:{" "}
                  <strong>
                    {formatDisplayDate(nextSiteVisitDate.toISOString())}
                  </strong>
                </Typography>
              </Alert>
            )}

            {/* No record or Pending — Show Schedule Form */}
            {(noRecord || isPending) && (
              <>
                {/* Date Selection */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    sx={{ mb: 1 }}
                  >
                    Select Date
                  </Typography>
                  <TextField
                    type="date"
                    fullWidth
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      min: new Date().toISOString().split("T")[0],
                    }}
                  />
                </Box>

                {/* AI Agent Toggle */}
                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: triggerAiAgent ? "primary.main" : "divider",
                    bgcolor: triggerAiAgent ? "action.hover" : "background.paper",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Trigger Site Visit AI Agent
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Automatically coordinate with assigned crew via AI agent on WhatsApp
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={triggerAiAgent}
                        onChange={(e) => setTriggerAiAgent(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={triggerAiAgent ? "ON" : "OFF"}
                    labelPlacement="start"
                    sx={{ m: 0 }}
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Selected Crew Chips */}
                <SelectedCrewChips
                  selectedMembers={selectedCrewMembers}
                  onRemove={handleDeselectMember}
                />

                {/* Crew Member List */}
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Select Crew Members
                </Typography>
                <CrewMemberList
                  selectedMembers={selectedCrewMembers}
                  onSelectMember={handleSelectMember}
                  onDeselectMember={handleDeselectMember}
                  apiService={apiService}
                />
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        {(noRecord || isPending) && (
          <Button
            variant="contained"
            onClick={handleScheduleSiteVisit}
            disabled={
              submitting || !scheduledDate || selectedCrewMembers.length === 0
            }
          >
            {submitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Schedule Site Visit"
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleSiteVisitModal;
